<template>
  <div class="page-wrap" :class="{ night }">
    <ResourceLoader />
    <AppHeader />

    <div class="lobby-main">
    <section
      class="collection"
      :class="{ collapsed: !collectionOpen }"
      :style="{ backgroundImage: `url(${collectionBg})` }"
    >
      <div class="cell">
        <div class="unfold-button" @click="collectionOpen = !collectionOpen">
          <img class="fav-icon" :src="favHex" alt="" />
          <div class="lines">
            <div class="line-1">收藏游戏</div>
            <a class="line-2" href="javascript:;" @click.stop.prevent="goFav">您的收藏{{ favCount }} &gt;</a>
          </div>
        </div>
      </div>
      <div
        v-for="item in collectionGames"
        :key="item.id"
        class="cell"
        @click="goBet(item.id)"
      >
        <div class="collected-item">
          <div class="game-name">{{ item.name }}</div>
          <div class="countdown">
            <img class="clock" :src="clockIcon" alt="" />
            <span class="time">{{ formatClock(item.remain) }}</span>
          </div>
          <div class="concurrent-users">
            游戏人数
            <img class="user-ico" :src="userCountIcon" alt="" />
            {{ formatWan(item.players) }}
          </div>
        </div>
      </div>
    </section>

    <main class="page-body">
      <section class="hot-section">
        <div class="hot-head">
          <div class="date-block">
            <span class="date-num">{{ today.day }}</span>
            <div class="date-meta">
              <span>{{ today.month }}</span>
              <span>{{ today.week }}</span>
            </div>
            <span class="hot-title"><i>‧</i>热门游戏</span>
          </div>
          <div class="hot-dots">
            <button
              v-for="(page, i) in hotPages"
              :key="i"
              class="dot"
              :class="{ active: hotDot === i }"
              type="button"
              @click="goHot(i)"
            />
          </div>
        </div>

        <div class="hot-viewport">
          <div
            class="hot-track"
            :class="{ instant: hotJump }"
            :style="{ transform: `translate3d(-${hotSlide * 100}%, 0, 0)` }"
            @transitionend="onHotEnd"
          >
            <div v-for="(page, pi) in hotLoopPages" :key="pi" class="hot-cards">
              <article
                v-for="game in page"
                :key="`${pi}-${game.id}`"
                class="hot-card"
                @click="goBet(game.id)"
              >
                <img class="hot-bg" :src="game.cover" :alt="game.name" />
                <div class="hot-info">
                  <h3>{{ game.name }}</h3>
                  <p v-if="game.tag" class="hot-tag">
                    <span class="hot-tag-text">{{ game.tag }}</span>
                    <img class="hot-tag-icon" :src="recArrow" alt="" />
                  </p>
                  <div class="hot-freq">
                    <span>{{ game.period }}</span>
                    <img class="user-ico" :src="userCountIcon" alt="" />
                    <span>{{ formatHotPlayers(game.players) }}</span>
                  </div>
                  <div class="hot-stars">
                    推荐指数
                    <img
                      v-for="n in 5"
                      :key="n"
                      class="star"
                      :src="n <= game.stars ? starOn : starOff"
                      alt=""
                    />
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <div class="ticker">
        <div class="ticker-track">
          <span v-for="(item, i) in tickerLoop" :key="i" class="ticker-item">
            <img class="ticker-icon" :src="ui.mascot" alt="" />
            <span v-html="formatTicker(item)" />
          </span>
        </div>
      </div>

      <section class="game-panel">
        <div class="game-tabs">
          <div class="tab-upper">
            <button
              v-for="(tab, index) in gameTabs"
              :key="tab"
              class="game-tab"
              :class="{ active: gameTab === index }"
              type="button"
              @click="gameTab = index"
            >
              {{ tab }}
            </button>
          </div>
          <div class="tab-lower"></div>
        </div>

        <!-- 红框区域：仅列表上下滚动，无滚动条；上方收藏/热门/跑马灯/Tab 固定 -->
        <div class="list-scroll">
          <ul class="game-list">
            <li v-for="game in currentGames" :key="game.id">
              <GameRow :game="game" />
            </li>
            <li v-if="!currentGames.length" class="game-list-empty">
              {{ emptyTip }}
            </li>
          </ul>
        </div>
      </section>
    </main>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import GameRow from '@/components/GameRow.vue'
import ResourceLoader from '@/components/ResourceLoader.vue'
import favHex from '@/assets/lobby/ui/fav-hex.svg'
import tickerMascot from '@/assets/lobby/ui/ticker-mascot.svg'
import collectionBg from '@/assets/lobby/ui/collection-bg.png'
import clockIcon from '@/assets/lobby/ui/countdown-clock.svg'
import userCountIcon from '@/assets/lobby/ui/user-count.svg'
import starOn from '@/assets/lobby/ui/star-on.svg'
import starOff from '@/assets/lobby/ui/star-off.svg'
import recArrow from '@/assets/lobby/ui/rec-arrow.svg'
import coverPK10 from '@/assets/lobby/popular/PK10.png'
import coverTXFFC from '@/assets/lobby/popular/TXFFC.png'
import coverSSC from '@/assets/lobby/popular/SSC.png'
import coverGPK10 from '@/assets/lobby/popular/GPK10.png'
import coverKENO from '@/assets/lobby/popular/KENO.png'
import coverMOPK10 from '@/assets/lobby/popular/MOPK10.png'
import coverPCB from '@/assets/lobby/popular/PCB.png'
import coverLF from '@/assets/lobby/popular/LF.png'
import coverYGSSC from '@/assets/lobby/popular/YGSSC.png'
import { games, tickerList, visibleGames, isGameVisible } from '@/data/games'
import { useTheme } from '@/composables/useTheme'
import { setActiveGame } from '@/composables/useActiveGame'
import { getBetPath } from '@/composables/betRoute'
import { useRecentGames } from '@/composables/useRecentGames'

const router = useRouter()
const { night } = useTheme()
const { recentGames } = useRecentGames()
const ui = { mascot: tickerMascot }

const gameTabs = ['推荐游戏', '最近游玩', '收藏TOP10']
const gameTab = ref(0)
const hotSlide = ref(0)
const hotJump = ref(false)
const collectionOpen = ref(false)
const favCount = computed(() => visibleGames.value.filter((g) => g.fav).length)

const weekMap = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const monthMap = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
const now = new Date()
const today = {
  day: String(now.getDate()).padStart(2, '0'),
  month: monthMap[now.getMonth()],
  week: weekMap[now.getDay()]
}

/** 大厅收藏条：仅生产上架彩种 */
const collectionGames = ref([
  { id: 15, name: '体彩P3P5', remain: 12 * 3600 + 20 * 60 + 37, players: 4114, cycle: 24 * 3600 },
  { id: 14, name: '福彩3D', remain: 6 * 3600, players: 18000, cycle: 24 * 3600 },
  { id: 17, name: '双色球', remain: 8 * 3600 + 15 * 60, players: 18000, cycle: 2 * 24 * 3600 },
  { id: 16, name: '超级大乐透', remain: 10 * 3600 + 30 * 60, players: 15000, cycle: 2 * 24 * 3600 },
  { id: 19503, name: '澳门飞艇', remain: 7, players: 87400, cycle: 300 },
  { id: 1103, name: '天成六合彩', remain: 11, players: 5000, cycle: 60 },
  { id: 1909, name: '天成快三', remain: 11, players: 136367, cycle: 60 },
  { id: 21503, name: '天成快乐8', remain: 25, players: 96512, cycle: 300 }
])

const hotGames = [
  { id: 21503, name: '天成快乐8', tag: '新增彩票', period: '5分钟/期', players: 96512, stars: 4, cover: coverKENO },
  { id: 19503, name: '澳门飞艇', tag: '金选彩票', period: '5分钟/期', players: 96096, stars: 4, cover: coverGPK10 },
  { id: 14, name: '福彩3D', tag: '热门销量彩票', period: '1天/期', players: 10622, stars: 4, cover: coverLF },
  { id: 16, name: '超级大乐透', tag: '热门销量彩票', period: '1,3,6/每周', players: 15000, stars: 4, cover: coverLF },
  { id: 17, name: '双色球', tag: '热门销量彩票', period: '2,4,0/每周', players: 18000, stars: 4, cover: coverLF },
  { id: 1909, name: '天成快三', tag: '热门销量彩票', period: '1分钟/期', players: 136367, stars: 4, cover: coverSSC },
  { id: 1103, name: '天成六合彩', tag: '中奖推荐排行', period: '1分钟/期', players: 5000, stars: 3, cover: coverKENO }
]

const hotPages = Array.from({ length: Math.ceil(hotGames.length / 2) }, (_, i) =>
  hotGames.slice(i * 2, i * 2 + 2)
)
const hotLoopPages = [...hotPages, hotPages[0]]
const hotDot = computed(() => hotSlide.value % hotPages.length)

const goHot = (index) => {
  hotSlide.value = index
  restartHotTimer()
}
const nextHot = () => {
  hotSlide.value += 1
}
const onHotEnd = (e) => {
  if (e.target !== e.currentTarget) return
  if (hotSlide.value < hotPages.length) return
  hotJump.value = true
  hotSlide.value = 0
  nextTick(() => {
    requestAnimationFrame(() => {
      hotJump.value = false
    })
  })
}

let hotTimer
const restartHotTimer = () => {
  clearInterval(hotTimer)
  hotTimer = setInterval(nextHot, 10000)
}

const tickerLoop = [...tickerList, ...tickerList]
const formatTicker = (text) => text.replace(/(\d+U)/g, '<em>$1</em>')

/** 大厅推荐列表：生产上架彩种 */
const lobbyIds = [14, 15, 17, 16, 1103, 19503, 1909, 21503]
const lobbyGames = computed(() =>
  lobbyIds.map((id) => games.value.find((g) => g.id === id)).filter((g) => g && isGameVisible(g))
)

const currentGames = computed(() => {
  // 0 推荐 / 1 最近游玩（下注成功后写入）/ 2 收藏 TOP10
  if (gameTab.value === 1) return recentGames.value
  if (gameTab.value === 2) return visibleGames.value.filter((g) => g.fav).slice(0, 10)
  return lobbyGames.value
})

const emptyTip = computed(() => {
  if (gameTab.value === 1) return '暂无最近游玩，去投注一笔就会出现在这里'
  if (gameTab.value === 2) return '暂无收藏，点击爱心即可加入'
  return '暂无推荐彩种'
})

const goFav = () => router.push({ path: '/category', query: { tab: 'fav' } })
const goBet = (id) => {
  setActiveGame(id)
  router.push({ path: getBetPath(id), query: { id } })
}

const formatWan = (n) => {
  if (n >= 10000) {
    const wan = (n / 10000).toFixed(2).replace(/\.?0+$/, '')
    return `${wan} 万人`
  }
  return `${n} 人`
}

const formatHotPlayers = (n) => `${Number(n || 0)}人`

const formatClock = (sec) => {
  const h = String(Math.floor(sec / 3600)).padStart(2, '0')
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0')
  const s = String(sec % 60).padStart(2, '0')
  return `${h}:${m}:${s}`
}

let timer
onMounted(() => {
  timer = setInterval(() => {
    collectionGames.value.forEach((item) => {
      item.remain = item.remain > 0 ? item.remain - 1 : item.cycle
    })
  }, 1000)
  restartHotTimer()
})
onUnmounted(() => {
  clearInterval(timer)
  clearInterval(hotTimer)
})
</script>

<style scoped>
/* 顶区固定 + 下方推荐列表滚动（无滚动条）；覆盖全局 bet-page-shell */
.page-wrap {
  position: relative;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  margin: 0 auto;
  background: #fff;
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  color: #1a1a1a;
  overflow: hidden !important;
}

.page-wrap :deep(.app-chrome) {
  flex-shrink: 0;
}

.lobby-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #2f2539;
  overflow: hidden;
}

.collection {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  padding: 4px 2px 6px;
  background-color: #2f2539;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 0;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  max-height: 230px;
  overflow: hidden;
  transition: max-height 0.4s ease-in-out;
}

.collection.collapsed {
  max-height: 92px;
}

.cell {
  width: 33.33%;
  height: 74px;
  box-sizing: border-box;
  padding: 2px 4px;
  transform: scale(0.96);
}

.unfold-button {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.fav-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: block;
}

.lines {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.line-1 {
  color: #fff;
  font-size: 15px;
  line-height: 1.1;
  white-space: nowrap;
}

.line-2 {
  color: #fff;
  opacity: 0.35;
  font-size: 11px;
  line-height: 1.1;
  text-decoration: none;
  white-space: nowrap;
}

.collected-item {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  cursor: pointer;
  padding-left: 2px;
}

.game-name {
  color: #f5c4a8;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.countdown {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;
  margin: 4px 0 4px -4px;
  padding: 0;
  align-self: flex-start;
}

.clock {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  margin: 0;
  opacity: 0.85;
  filter: brightness(0) invert(0.75);
}

.time {
  color: #bbb;
  font-size: 12px;
  letter-spacing: 0.4px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.concurrent-users {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 10px;
  white-space: nowrap;
  line-height: 1.2;
  align-self: flex-start;
}

.user-ico {
  width: 11px;
  height: 11px;
  margin: 0 3px;
  flex-shrink: 0;
  display: inline-block;
  object-fit: contain;
}

.page-body {
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 10px 12px 0;
  overflow: hidden;
}

.hot-section {
  flex-shrink: 0;
}

.hot-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  min-height: 36px;
}

.date-block {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.date-num {
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  color: #222;
}

.date-meta {
  display: flex;
  flex-direction: column;
  font-size: 10px;
  color: #8a8f99;
  line-height: 1.25;
}

.hot-title {
  margin-left: 4px;
  font-size: 15px;
  font-weight: 700;
  color: #222;
  white-space: nowrap;
}

.hot-title i {
  font-style: normal;
  margin-right: 1px;
}

.hot-dots {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 5px;
}

.dot {
  width: 6px;
  height: 6px;
  padding: 0;
  border: 0;
  border-radius: 99px;
  background: #d8dbe3;
  cursor: pointer;
}

.dot.active {
  width: 18px;
  background: #d3d3d3;
}

.hot-viewport {
  overflow: hidden;
  width: 100%;
}

.hot-track {
  display: flex;
  width: 100%;
  transition: transform 0.45s ease;
}

.hot-track.instant {
  transition: none;
}

.hot-cards {
  flex: 0 0 100%;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.hot-card {
  position: relative;
  height: 108px;
  border-radius: 8px;
  overflow: hidden;
  color: #fff;
  cursor: pointer;
}

.hot-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center right;
  transform: scale(1.08);
}

.hot-info {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  height: 100%;
  padding: 8px 10px 7px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 3px;
}

.hot-card h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.15;
}

.hot-tag {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 2px;
  max-width: calc(100% - 4px);
  height: 16px;
  padding: 0 8px 0 6px;
  border-radius: 3px;
  background: linear-gradient(115deg, #f5c4a8, #fbdbcb);
  clip-path: polygon(100% 60%, calc(100% - 7px) 100%, 0 100%, 0 0, 100% 0);
  white-space: nowrap;
}

.hot-tag-text {
  color: #464646;
  font-size: 10px;
  line-height: 16px;
}

.hot-tag-icon {
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  display: block;
}

.hot-freq {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  line-height: 1.2;
  color: #fff;
  transform: scale(0.92);
  transform-origin: left center;
}

.hot-stars {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: auto;
  font-size: 10px;
  color: #fff;
  transform: scale(0.92);
  transform-origin: left center;
}

.star {
  width: 10px;
  height: 10px;
}

.ticker {
  flex-shrink: 0;
  margin: 12px 0 14px;
  overflow: hidden;
  height: 34px;
}

.ticker-track {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  width: max-content;
  animation: marquee 22s linear infinite;
}

.ticker-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 12px 0 5px;
  background: #ecebed;
  border-radius: 99px;
  font-size: 12px;
  color: #343b42;
  white-space: nowrap;
}

.ticker-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: block;
  object-fit: contain;
}

.ticker-item :deep(em) {
  color: #f42c4c;
  font-style: normal;
  font-weight: 700;
}

@keyframes marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

.game-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}

.game-tabs {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  background: linear-gradient(180deg, #ffffff, #edeef4);
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.tab-upper {
  display: flex;
  justify-content: space-evenly;
  height: 40px;
}

.game-tab {
  position: relative;
  flex: 0 1 22%;
  padding: 0;
  border: 0;
  background: none;
  font-size: 14px;
  line-height: 40px;
  color: #898e91;
  cursor: pointer;
}

.game-tab.active {
  color: #343b42;
  font-weight: 700;
}

.game-tab.active::after {
  content: '';
  position: absolute;
  left: -10%;
  right: -10%;
  top: 46px;
  height: 8px;
  border-radius: 6px 6px 0 0;
  background-image: linear-gradient(90deg, #ff559c 0%, #b94aff 51%, #6891ff 100%, #3b70ff 100%);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.tab-lower {
  position: relative;
  height: 14px;
  overflow: hidden;
  background: #d5d5d5;
  opacity: 0.6;
  border-radius: 0 0 0 8px;
}

.tab-lower::before {
  content: '';
  position: absolute;
  left: -14px;
  width: 40px;
  height: 40px;
  transform: rotate(36deg);
  background-image: linear-gradient(-47deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  opacity: 0.7;
}

.list-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding-bottom: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.list-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.game-list {
  list-style: none;
  margin: 0;
  padding: 0 6px 6px;
  background: #fff;
}

.game-list li {
  border-bottom: 1px solid #dddfed;
}

.game-list li:last-child {
  border-bottom: 0;
}

.game-list-empty {
  padding: 40px 16px;
  text-align: center;
  color: #999;
  font-size: 13px;
  line-height: 1.5;
  border-bottom: 0 !important;
}

.game-list :deep(.game-row) {
  padding: 10px 4px;
}

.night.page-wrap {
  background: #2c2e35;
}

.night .page-body,
.night .game-panel,
.night .game-list {
  background: #2c2e35;
}

.night .game-list-empty {
  color: #8a8494;
}

.night .date-num,
.night .hot-title,
.night .game-tab.active {
  color: #fff;
}

.night .date-meta,
.night .game-tab {
  color: #9aa0a8;
}

.night .ticker-item {
  background: #474747;
  color: #fff;
}

.night .game-tabs {
  background: linear-gradient(180deg, #1f1f1f 0%, #363636 100%);
  box-shadow: none;
}

.night .tab-lower {
  background: #0e0e0e;
  opacity: 1;
}

.night .tab-lower::before {
  background: #2d2d2d;
}

.night .game-list li {
  border-color: #3f424a;
}

.night .dot {
  background: #5a5e68;
}

.night .lobby-main,
.night .collection {
  background-color: #261c30;
}
</style>
